import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReducedMotion } from 'framer-motion'
import { Pause, Play } from 'lucide-react'

/** Il video del videomaker (≈1 minuto, muto, in loop): va messo in apps/frontend/public/media/. */
const HERO_VIDEO_SRC = '/media/hero.mp4'

/**
 * Sfondo dell'hero a tutta viewport. Finché il file non c'è (o non si carica) resta lo sfondo "notte di festa"
 * della sezione, con gli sticker. Con "riduci movimento" il video parte fermo; il pulsante lo ferma e lo riavvia
 * (contenuto in movimento oltre 5 secondi: serve un comando per metterlo in pausa).
 */
export function HeroVideo() {
  const { t } = useTranslation()
  const prefersReducedMotion = useReducedMotion()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isMissing, setIsMissing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  // Il pulsante compare solo quando il video c'è davvero: niente lampo "Riproduci" prima dell'errore.
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (prefersReducedMotion) {
      video.pause()
      return
    }
    // Il rifiuto (autoplay bloccato dal browser) lascia il video fermo sul primo fotogramma.
    video.play().catch(() => setIsPlaying(false))
  }, [prefersReducedMotion])

  const toggle = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) video.play().catch(() => setIsPlaying(false))
    else video.pause()
  }

  return (
    <>
      <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
        {isMissing ? null : (
          <video
            ref={videoRef}
            src={HERO_VIDEO_SRC}
            muted
            loop
            playsInline
            preload="auto"
            className="size-full object-cover"
            onLoadedData={() => setIsReady(true)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={() => setIsMissing(true)}
          />
        )}
        {/* Velatura dal basso: il titolo resta leggibile su qualunque fotogramma. */}
        <div className="absolute inset-0 bg-[linear-gradient(to_top,color-mix(in_oklch,var(--color-ink)_85%,transparent)_0%,color-mix(in_oklch,var(--color-ink)_40%,transparent)_45%,transparent_75%)]" />
      </div>
      {!isMissing && isReady && (
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? t('home.hero.pauseVideo') : t('home.hero.playVideo')}
          className="absolute top-20 right-4 z-10 grid size-10 place-items-center rounded-full bg-ink/40 text-paper backdrop-blur-sm transition-colors hover:bg-ink/70 sm:right-6 md:top-24 lg:right-8"
        >
          {isPlaying ? <Pause className="size-4" aria-hidden="true" /> : <Play className="size-4" aria-hidden="true" />}
        </button>
      )}
    </>
  )
}
